import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import { APP_NAME } from '../config/branding';
import { Loader2, CheckCircle, AlertTriangle, PenTool, AlertCircle } from 'lucide-react';
import { ConsentForm } from '../types/encounter';

const SignConsent: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [form, setForm] = useState<ConsentForm | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [signerName, setSignerName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    useEffect(() => {
        if (token) {
            fetchFormDetails();
        }
    }, [token]);

    useEffect(() => {
        if (isSuccess) {
            triggerConfetti();
        }
    }, [isSuccess]);

    const triggerConfetti = () => {
        const colors = ['#EF4444', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6'];
        const confettiCount = 100;

        for (let i = 0; i < confettiCount; i++) {
            const confetti = document.createElement('div');
            confetti.className = 'confetti';
            confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
            confetti.style.left = Math.random() * 100 + 'vw';
            confetti.style.animationDuration = Math.random() * 3 + 2 + 's';
            confetti.style.opacity = Math.random().toString();
            document.body.appendChild(confetti);

            setTimeout(() => {
                confetti.remove();
            }, 5000);
        }
    };

    const fetchFormDetails = async () => {
        try {
            const response = await fetch(API_ENDPOINTS.ENCOUNTER_CONSENT.GET_PUBLIC(token!));
            if (response.ok) {
                const data = await response.json();
                setForm(data);
                if (data.status === 'signed') {
                    setIsSuccess(true);
                }
            } else {
                setError('Document not found or link expired');
            }
        } catch (err) {
            setError('Failed to load document');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSign = async () => {
        if (!signerName.trim()) {
            setSubmitError('Please enter your full name');
            return;
        }

        setIsSubmitting(true);
        setSubmitError(null);

        try {
            const signatureData = `Signed by: ${signerName}`;

            const response = await fetch(API_ENDPOINTS.ENCOUNTER_CONSENT.SIGN_PUBLIC(token!), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    signature_data: signatureData,
                    signature_type: 'type',
                    signer_name: signerName
                })
            });

            if (response.ok) {
                const updatedForm = await response.json();
                setForm(updatedForm);
                setIsSuccess(true);
            } else {
                const errData = await response.json();
                // Handle object error details safely
                let errorMessage = 'Failed to submit signature';
                if (typeof errData.detail === 'string') {
                    errorMessage = errData.detail;
                } else if (Array.isArray(errData.detail)) {
                    errorMessage = errData.detail.map((e: any) => e.msg).join(', ');
                } else if (typeof errData.detail === 'object') {
                    errorMessage = JSON.stringify(errData.detail);
                }
                setSubmitError(errorMessage);
            }
        } catch (err) {
            console.error('Signing error:', err);
            setSubmitError('Failed to submit signature. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
        );
    }

    if (error || !form) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
                <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
                    <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Document</h2>
                    <p className="text-gray-600">{error || 'Document not found'}</p>
                </div>
            </div>
        );
    }

    if (isSuccess) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4 overflow-hidden relative">
                <style>{`
          .confetti {
            position: fixed;
            top: -10px;
            width: 10px;
            height: 10px;
            z-index: 100;
            animation: fall linear forwards;
          }
          @keyframes fall {
            to {
              transform: translateY(100vh) rotate(720deg);
            }
          }
        `}</style>
                <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-10 text-center relative z-10 border border-gray-100">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                        <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>

                    <h2 className="text-3xl font-bold text-gray-900 mb-2">Complete!</h2>
                    <p className="text-gray-600 mb-8 text-lg">
                        Thank you, {signerName || form.signer_info?.name || 'Customer'}.<br />
                        The document has been successfully signed.
                    </p>

                    <div className="bg-gray-50 rounded-xl p-6 text-left border border-gray-200">
                        <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1">Document Hash</div>
                        <div className="text-xs text-gray-600 font-mono break-all bg-white p-2 rounded border border-gray-200">
                            {form.signature_hash || 'Generating hash...'}
                        </div>
                        <div className="mt-4 flex items-center text-xs text-gray-400">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Securely recorded on {new Date().toLocaleDateString()}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 flex flex-col">
            {/* Header */}
            <header className="bg-white shadow-sm px-6 py-4 sticky top-0 z-10">
                <div className="max-w-6xl mx-auto flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-sm">
                            <PenTool className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h1 className="text-lg font-bold text-gray-900 leading-tight">Sign Document</h1>
                            <div className="text-sm text-gray-500 truncate max-w-xs sm:max-w-md">
                                {form.filename}
                            </div>
                        </div>
                    </div>
                    <div className="hidden sm:block text-xs text-gray-400 bg-gray-50 px-3 py-1 rounded-full border border-gray-200">
                        Secure Signing
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-1 max-w-6xl w-full mx-auto p-4 sm:p-6 flex flex-col lg:flex-row gap-6">
                {/* PDF Viewer (Left/Top) */}
                <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden min-h-[500px] lg:min-h-[calc(100vh-140px)] relative">
                    <iframe
                        src={`${form.url}#toolbar=0&navpanes=0&scrollbar=0`}
                        className="w-full h-full border-0 absolute inset-0"
                        title="PDF Document"
                    />
                </div>

                {/* Signing Panel (Right/Bottom) */}
                <div className="w-full lg:w-96 flex-shrink-0 space-y-6">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-24">
                        <h3 className="text-xl font-semibold text-gray-900 mb-6">Your Signature</h3>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Full Name
                                </label>
                                <input
                                    type="text"
                                    value={signerName}
                                    onChange={(e) => setSignerName(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
                                    placeholder="Type your full name"
                                    disabled={form.status === 'signed'}
                                />
                            </div>

                            {submitError && (
                                <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-md flex items-start">
                                    <AlertCircle className="w-5 h-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-red-700">{submitError}</p>
                                </div>
                            )}

                            <div className="pt-6 border-t border-gray-100">
                                <p className="text-xs text-gray-500 mb-6 leading-relaxed">
                                    By clicking "Sign Document", you agree to be legally bound by this document and the {APP_NAME} Terms of Service. A digital fingerprint of this signature will be recorded.
                                </p>

                                <button
                                    onClick={handleSign}
                                    disabled={!signerName.trim() || isSubmitting}
                                    className="w-full py-3.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all shadow-sm hover:shadow-md flex items-center justify-center"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                            Signing...
                                        </>
                                    ) : (
                                        'Sign Document'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default SignConsent;
